'use client'

import { BuilderBlocks } from '@builder.io/react'
import { uniqueId } from 'lodash'
import { useMemo, useState } from 'react'

import { Dropdown } from '@src/components/Dropdown/Dropdown'
import { type DropdownItem } from '@src/components/Dropdown/Dropdown'
import { NutritionalFactsDefaults } from '@src/components/NutritionalFacts/NutritionalFacts.defaults'
import { type NutritionalFactsProps } from '@src/components/NutritionalFacts/NutritionalFacts.props'
import { NutritionalFactsContent } from '@src/components/NutritionalFacts/NutritionalFactsContent'
import { getBuilderPreviewMode } from '@src/utils/builder.util'
import { cn } from '@src/utils/tailwind'

export const NutritionalFacts = (props: NutritionalFactsProps = NutritionalFactsDefaults) => {
  const { builderBlock, items, previewAllItems, heading, skipWrapper, singleColumn } = props

  const [selectedFlavorName, setSelectedFlavorName] = useState<string | undefined>(
    items?.[0]?.flavorName
  )

  const nutritionalInfo = useMemo(() => {
    const defaultNutritionalInfo = items?.[0] || undefined

    const selectedNutritionalInfo = items.find((item) => item.flavorName === selectedFlavorName)

    if (!selectedFlavorName || !selectedNutritionalInfo) return defaultNutritionalInfo

    return selectedNutritionalInfo
  }, [items, selectedFlavorName])

  const renderDropdown = () => {
    const dropdownItems: DropdownItem[] = items.map((item) => {
      const name = item.flavorName ?? ''

      return {
        id: name,
        name,
      }
    })

    return (
      <Dropdown
        selectedId={nutritionalInfo?.flavorName as string}
        items={dropdownItems}
        onItemIdSelected={(id) => {
          setSelectedFlavorName(id as string)
        }}
      />
    )
  }

  const renderContent = () => {
    const { edit } = getBuilderPreviewMode()
    const shouldShowAllItems = previewAllItems && edit

    if (shouldShowAllItems)
      return (
        <>
          {items.map((info) => {
            return (
              <div key={uniqueId()} className="my-4 flex flex-col gap-2">
                <div className="font-semibold uppercase text-bodi-gray-dark">
                  {info.flavorName}:
                </div>
                <hr />
                <BuilderBlocks
                  parentElementId={builderBlock?.id}
                  dataPath={`component.options.${info?.flavorName}.subheading.content`}
                  blocks={info?.blocksSubheading}
                />
                <NutritionalFactsContent
                  singleColumn={singleColumn}
                  nutritionalInfo={info}
                  builderBlock={builderBlock}
                />
              </div>
            )
          })}
        </>
      )

    return (
      <NutritionalFactsContent
        singleColumn={singleColumn}
        nutritionalInfo={nutritionalInfo}
        builderBlock={builderBlock}
      />
    )
  }

  return (
    <div
      className={cn({
        'rounded-3xl border border-gray-300 px-8 py-12 lg:p-16': !skipWrapper,
      })}
    >
      <div className="flex flex-col justify-between gap-4 border-b border-bodi-gray-butters pb-4 lg:flex-row lg:items-center lg:gap-0">
        <div className="flex flex-col gap-2">
          <div
            className="text-heading-display-2"
            dangerouslySetInnerHTML={{ __html: heading ?? '' }}
          ></div>
          <BuilderBlocks
            parentElementId={builderBlock?.id}
            dataPath={`component.options.${nutritionalInfo?.flavorName}.subheading.content`}
            blocks={nutritionalInfo?.blocksSubheading}
          />
        </div>
        {items.length > 1 && <div className="w-full shrink-0 lg:w-[270px]">{renderDropdown()}</div>}
      </div>

      {renderContent()}
    </div>
  )
}
